import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Trophy, Users, Clock, Heart, MapPin,
  ArrowRight, ShoppingCart, Star, Target, Zap, Cpu, Check,
  Hammer, Wrench, ShoppingBag, CalendarRange, GraduationCap, Sparkles,
  ShieldCheck, Megaphone,
} from "lucide-react";
import { api } from "@/lib/api";
import BookLink from "@/components/site/BookLink";
import { useSettings } from "@/lib/settings";

const ICON_MAP = {
  Trophy, Users, Clock, Heart, Target, Zap, Sparkles, Cpu, ShieldCheck,
};
const iconFor = (name) => ICON_MAP[name] || Trophy;

const defaultHeroSlides = [
  {
    // CRICKET — red leather cricket ball on grass (Patrick Case · Unsplash)
    image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=2000&q=80",
    eyebrow: "Indoor Cricket · Mississauga",
    title: "Train Like A",
    titleAccent: "Champion.",
    body: "14,000 sq ft indoor facility. 8 pro-grade lanes. Bola & Iron Mike machines. Built for players who don't make excuses.",
  },
  {
    // BASEBALL — baseball on pitcher's mound dirt
    image: "https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&w=2000&q=80",
    eyebrow: "Baseball Batting Cages",
    title: "Future Champions",
    titleAccent: "Start Here.",
    body: "Pro-grade machines. Certified coaches. A community built for serious cricket & baseball players. Step into the cages today.",
  },
  {
    // FOOTBALL — soccer ball on green grass
    image: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=2000&q=80",
    eyebrow: "Cricket · Baseball · Football",
    title: "Your Lane.",
    titleAccent: "Your Time.",
    body: "Lock a single lane, a full box, or grab the squad. Real-time availability, no phone calls, no waiting.",
  },
];

const defaultBenefits = [
  { icon: Trophy, title: "Elite Facilities", body: "Train in a 14,000 sq ft facility built for professional cricket performance." },
  { icon: Users, title: "Certified Coaches", body: "Learn from professionals dedicated to nurturing the next generation of cricket stars." },
  { icon: Clock, title: "Bola Machines", body: "Use the world's leading bowling machines for precise and repetitive practice." },
  { icon: Heart, title: "Active Community", body: "Join a vibrant community of cricket enthusiasts and future champions in Mississauga." },
];

const defaultHowItWorks = [
  { n: "01", title: "Join Us", body: "Create your account and join Mississauga's premier cricket community for exclusive booking rates." },
  { n: "02", title: "Select a Lane", body: "Choose from our 14,000 sq ft facility featuring professional-grade lanes and bowling machines." },
  { n: "03", title: "Booking Process", body: "Book your preferred time slot and equipment instantly with our seamless online system." },
];

const proShopItems = [
  // CRICKET — batsman / ball / pads / gear
  { name: "Cricket Bats", price: 149, image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&w=900&q=80" },
  { name: "Batting Pads", price: 79.99, image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=900&q=80" },
  // BASEBALL — glove / stadium / bat
  { name: "Baseball Gloves", price: 45.5, image: "https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&w=900&q=80" },
  { name: "Helmets", price: 89, image: "https://images.unsplash.com/photo-1566577739112-5180d4bf9390?auto=format&fit=crop&w=900&q=80" },
  // FOOTBALL — soccer ball / pitch
  { name: "Football Boots", price: 110, image: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=900&q=80" },
  { name: "Match Footballs", price: 95, image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=900&q=80" },
];

// Reusable section heading with Orbitron + uppercase + tracking
const SectionEyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-3 font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.4em] text-spike-blue-light">
    <span className="w-8 h-px bg-spike-blue" /> {children} <span className="w-8 h-px bg-spike-blue" />
  </span>
);

const SectionTitle = ({ children, accent }) => (
  <h2 className="mt-5 font-orbitron font-black uppercase text-4xl sm:text-5xl text-spike-text leading-[1.05] tracking-tight">
    {children} {accent && <span className="text-spike-blue-light">{accent}</span>}
  </h2>
);

const Home = () => {
  const [slideIdx, setSlideIdx] = useState(0);
  const [machines, setMachines] = useState([]);
  const [cms, setCms] = useState(null);

  const heroSlides = (cms?.hero_slides?.length ? cms.hero_slides.map((s) => ({
    image: s.image,
    eyebrow: s.eyebrow,
    title: s.title,
    titleAccent: s.title_accent,
    body: s.body,
  })) : defaultHeroSlides);
  const benefits = (cms?.benefits?.length ? cms.benefits.map((b) => ({
    icon: iconFor(b.icon),
    title: b.title,
    body: b.body,
  })) : defaultBenefits);
  const howItWorks = (cms?.how_it_works?.length ? cms.how_it_works : defaultHowItWorks);
  const announcement = cms?.announcement || { enabled: false };

  const { show_membership, show_shop, shop_iframe_url } = useSettings();

  const slide = heroSlides[slideIdx] || heroSlides[0];

  useEffect(() => {
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/machines");
        setMachines(data);
      } catch {
        // silent — section will simply not render if API fails
      }
      try {
        const { data } = await api.get("/home/content");
        setCms(data);
      } catch {
        setCms(null);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page" className="bg-black">
      {/* Announcement banner (CMS-controlled) */}
      {announcement.enabled && announcement.text && (
        <div className="bg-spike-blue text-black" data-testid="home-announcement">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2.5 text-sm font-orbitron font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-3 text-center">
            <Megaphone className="w-4 h-4 shrink-0" />
            {announcement.link ? (
              <a href={announcement.link} target="_blank" rel="noopener noreferrer" className="hover:underline">{announcement.text}</a>
            ) : (
              <span>{announcement.text}</span>
            )}
          </div>
        </div>
      )}

      {/* HERO CAROUSEL */}
      <section className="relative h-[640px] lg:h-[720px] overflow-hidden" data-testid="hero-carousel">
        {heroSlides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === slideIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img src={s.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
            <div className="absolute inset-0 stripe-pattern opacity-50" />
          </div>
        ))}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-3 font-orbitron text-[0.7rem] font-bold text-spike-blue-light tracking-[0.4em] uppercase mb-6">
                <MapPin className="w-3.5 h-3.5" />
                {slide.eyebrow}
              </div>
              <h1 className="font-orbitron font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[1] text-spike-text tracking-tight">
                {slide.title} <br />
                <span className="text-spike-blue-light text-glow-blue">{slide.titleAccent}</span>
              </h1>
              <p className="mt-6 font-exo text-lg text-spike-text-muted max-w-xl leading-relaxed">{slide.body}</p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-row">
                <BookLink
                  
                  data-testid="hero-cta-book"
                  className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 transition-colors shadow-lg shadow-spike-blue/40 hover:shadow-spike-blue/70"
                >
                  Book a Lane <ArrowRight className="w-4 h-4" />
                </BookLink>
                <Link
                  to="/membership"
                  data-testid="hero-cta-membership"
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-spike-blue/10 text-spike-text font-orbitron font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 transition-colors border border-spike-blue/60"
                  style={show_membership ? {} : { display: "none" }}
                >
                  View Memberships
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel controls */}
        <button
          type="button"
          data-testid="hero-prev"
          onClick={() => setSlideIdx((i) => (i - 1 + heroSlides.length) % heroSlides.length)}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/40 border border-spike-blue/30 hover:bg-spike-blue/20 backdrop-blur-md items-center justify-center transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-spike-text" />
        </button>
        <button
          type="button"
          data-testid="hero-next"
          onClick={() => setSlideIdx((i) => (i + 1) % heroSlides.length)}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/40 border border-spike-blue/30 hover:bg-spike-blue/20 backdrop-blur-md items-center justify-center transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-spike-text" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`hero-dot-${i}`}
              onClick={() => setSlideIdx(i)}
              className={`h-1 transition-all ${i === slideIdx ? "w-10 bg-spike-blue" : "w-4 bg-spike-text/30"}`}
            />
          ))}
        </div>
      </section>

      {/* OUR BENEFITS */}
      <section className="relative py-20 lg:py-24 border-b border-spike-blue/10" data-testid="benefits-section">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <SectionEyebrow>Why Spike</SectionEyebrow>
            <SectionTitle accent="Spike Sports">Why</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg">Built for cricket, baseball &amp; futsal — under one roof.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} data-testid={`benefit-${b.title.replace(/\s+/g, "-").toLowerCase()}`} className="bg-spike-elevated border border-white/5 hover:border-spike-blue/40 p-7 transition-colors text-center group">
                  <div className="w-14 h-14 mx-auto bg-spike-blue/10 border border-spike-blue/30 flex items-center justify-center mb-5 group-hover:bg-spike-blue/20 transition-colors">
                    <Icon className="w-6 h-6 text-spike-blue-light" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-orbitron font-bold uppercase tracking-[0.1em] text-base text-spike-text">{b.title}</h3>
                  <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>

          {/* ===== ONE-STOP SHOP HIGHLIGHT ===== */}
          <div className="mt-20 lg:mt-24" data-testid="one-stop-shop-section">
            <div className="text-center mb-12">
              <SectionEyebrow>Everything Under One Roof</SectionEyebrow>
              <SectionTitle accent="Stop Shop">One</SectionTitle>
              <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
                Six services, one address. From the moment your bat needs knocking to the moment your team lifts the trophy — we cover it all.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
              {[
                { icon: Hammer,         title: "Bat Knocking",         body: "Pro-grade knocking-in service to extend bat life and boost middling performance." },
                { icon: Wrench,         title: "Repairs & Restrings",  body: "Cracks, handle re-grips, blade restoration, pad re-stitching — turn-around in 24–48h." },
                { icon: ShoppingBag,    title: "Shop Gear",            body: "Bats, pads, gloves, helmets, kit-bags & shoes — try before you buy in-store." },
                { icon: CalendarRange,  title: "Team Event Booking",   body: "Birthdays, corporate days, league nights, fundraisers — we handle the logistics." },
                { icon: GraduationCap,  title: "Coaching",             body: "Certified coaches across cricket, baseball & futsal — 1:1, group & academy plans." },
                { icon: Sparkles,       title: "Camps for Enthusiasts", body: "Multi-day skill camps for kids and adults — limited spots, big improvements." },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.title}
                    data-testid={`one-stop-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-spike-elevated p-8 hover:bg-black transition-colors group"
                  >
                    <div className="w-12 h-12 bg-spike-blue/15 border border-spike-blue/30 flex items-center justify-center group-hover:bg-spike-blue/30 transition-colors">
                      <Icon className="w-5 h-5 text-spike-blue-light" strokeWidth={2.2} />
                    </div>
                    <h3 className="mt-5 font-orbitron font-bold uppercase tracking-[0.12em] text-base text-spike-text">
                      {s.title}
                    </h3>
                    <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed">{s.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SPORTS WE PLAY — Cricket / Baseball / Futsal ===== */}
      <section className="relative py-20 lg:py-24 border-b border-spike-blue/10" data-testid="sports-showcase-section">
        <div className="absolute inset-0 stripe-pattern opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <SectionEyebrow>Sports We Run</SectionEyebrow>
            <SectionTitle accent="Three Sports">All</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
              Cricket lanes, baseball cages and a regulation futsal court — every winter, every weekend, every level.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tag: "Indoor Cricket",
                title: "8 Pro Lanes",
                copy: "Bola Pro & Iron Mike machines, swing & seam variations, league-grade run-ups. Cricket is where Spike was born — and where it shines.",
                points: ["8 lanes / 14,000 sq ft", "Bola Professional machine", "League play & academies"],
                image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=1200&q=80",
                cta: "Book a cricket lane",
              },
              {
                tag: "Baseball Cages",
                title: "Iron Mike Pitching",
                copy: "True overhand release up to 100 mph. Game-pace reps, coach-controlled cadence, and the open weekend cage hour every team needs.",
                points: ["Iron Mike MP-6 cage", "Speeds up to 100 mph", "Hitting & pitching coaches"],
                image: "https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&w=1200&q=80",
                cta: "Book the cage",
              },
              {
                tag: "Futsal Court",
                title: "5-a-side Indoor",
                copy: "Regulation indoor futsal court — fast surface, real glass back-net rebounders, weekly social leagues and pickup games every Friday night.",
                points: ["Full futsal court (open hours)", "Rebound walls + nets", "Leagues & casual pickup"],
                image: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=1200&q=80",
                cta: "Reserve the court",
              },
            ].map((s) => (
              <article
                key={s.title}
                data-testid={`sport-card-${s.tag.replace(/\s+/g, "-").toLowerCase()}`}
                className="group bg-spike-elevated border border-white/5 hover:border-spike-blue/50 overflow-hidden flex flex-col transition-colors"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <span className="absolute top-4 left-4 bg-spike-blue text-black font-orbitron text-[0.6rem] font-bold uppercase tracking-[0.25em] px-3 py-1">
                    {s.tag}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-orbitron font-black uppercase text-xl text-spike-text tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed">{s.copy}</p>
                  <ul className="mt-4 space-y-1.5 font-exo text-sm text-spike-text">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={2.5} />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <BookLink
                    
                    data-testid={`sport-cta-${s.tag.replace(/\s+/g, "-").toLowerCase()}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-transparent border border-spike-blue text-spike-blue-light hover:bg-spike-blue hover:text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-4 py-3 transition-colors"
                  >
                    {s.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </BookLink>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* EXPLORE OUR SERVICES — Lane Rental highlight */}
      <section className="py-20 lg:py-24 border-b border-spike-blue/10" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <SectionEyebrow>What We Offer</SectionEyebrow>
            <SectionTitle accent="Services">Explore Our</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
              Dedicated to excellence in cricket training, providing the best facilities for players and coaches alike.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-orbitron font-black uppercase text-3xl text-spike-text tracking-tight">Lane Rental</h3>
              <p className="mt-4 font-exo text-spike-text-muted leading-relaxed">
                Our professional indoor cricket lanes are equipped with the latest technology, including Bola Professional and
                Iron Mike bowling machines. Whether you&apos;re practicing your cover drive or perfecting your yorkers, our lanes
                provide the ideal surface and space for focused improvement.
              </p>
              <div className="mt-6">
                <h4 className="font-orbitron text-xs font-bold uppercase tracking-[0.25em] text-spike-blue-light mb-3">Our Benefits</h4>
                <ul className="space-y-2 font-exo text-sm text-spike-text-muted">
                  {[
                    "Advanced bowling machines for consistent and challenging practice.",
                    "High-quality turf and professional-grade netting systems.",
                    "Expert coaches available for one-on-one and group training.",
                    "Spacious 14,000 sq ft facility for year-round indoor cricket.",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Target className="w-4 h-4 text-spike-blue-light shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <BookLink
                
                data-testid="services-cta-explore"
                className="mt-8 inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.18em] text-xs px-6 py-3.5 transition-colors shadow-md shadow-spike-blue/30"
              >
                Explore Lanes <ArrowRight className="w-4 h-4" />
              </BookLink>
            </div>
            <div className="relative overflow-hidden border border-spike-blue/20 shadow-card-hover">
              <img
                src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&w=1400&q=80"
                alt="Cricket batsman in action"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* MACHINES — admin-managed */}
      {machines.length > 0 && (
        <section className="relative py-20 lg:py-24 border-b border-spike-blue/10" data-testid="machines-section">
          <div className="absolute inset-0 grid-pattern opacity-30" />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <SectionEyebrow>Our Arsenal</SectionEyebrow>
              <SectionTitle accent="Machines">Pro-Grade</SectionTitle>
              <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
                The same machines used by professionals — managed and maintained on-site so you always train against game pace.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {machines.map((m) => (
                <article
                  key={m.id}
                  data-testid={`machine-card-${m.id}`}
                  className="group bg-spike-elevated border border-white/5 hover:border-spike-blue/50 overflow-hidden flex flex-col transition-colors"
                >
                  <div className="relative overflow-hidden h-56">
                    {m.image_url ? (
                      <img
                        src={m.image_url}
                        alt={m.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-spike-blue/10 flex items-center justify-center">
                        <Cpu className="w-12 h-12 text-spike-blue/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-spike-blue text-black font-orbitron text-[0.6rem] font-bold uppercase tracking-[0.2em] px-3 py-1">
                      <Cpu className="w-3 h-3" /> {m.sport}
                    </span>
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-orbitron font-black uppercase text-lg text-spike-text tracking-tight">
                      {m.name}
                    </h3>
                    {m.tagline && (
                      <p className="mt-1 font-orbitron text-[0.7rem] font-bold uppercase tracking-[0.18em] text-spike-blue-light">
                        {m.tagline}
                      </p>
                    )}
                    {m.description && (
                      <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed">
                        {m.description}
                      </p>
                    )}
                    {m.specs?.length > 0 && (
                      <ul className="mt-4 space-y-2 font-exo text-sm text-spike-text-muted">
                        {m.specs.map((s, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-spike-blue-light shrink-0 mt-0.5" strokeWidth={2.5} />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-12 text-center">
              <BookLink
                
                data-testid="machines-cta-book"
                className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 transition-colors shadow-lg shadow-spike-blue/40 hover:shadow-spike-blue/70"
              >
                Train On These Machines <ArrowRight className="w-4 h-4" />
              </BookLink>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS (01 / 02 / 03) */}
      <section className="relative py-20 lg:py-24 border-b border-spike-blue/10" data-testid="how-it-works-section">
        <div className="absolute inset-0 stripe-pattern opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <SectionEyebrow>Three Steps</SectionEyebrow>
            <SectionTitle accent="Works">How It</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg">Three steps from couch to crease.</p>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-spike-blue/30" />
            {howItWorks.map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="text-center relative">
                <div className="mx-auto w-24 h-24 bg-black border-2 border-spike-blue text-spike-blue-light font-orbitron font-black text-2xl flex items-center justify-center shadow-lg shadow-spike-blue/30 relative z-10">
                  {s.n}
                </div>
                <h3 className="mt-5 font-orbitron font-bold uppercase tracking-[0.12em] text-lg text-spike-text">{s.title}</h3>
                <p className="mt-3 font-exo text-sm text-spike-text-muted leading-relaxed max-w-xs mx-auto">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED LANES */}
      <section className="py-20 lg:py-24 border-b border-spike-blue/10" data-testid="featured-lanes-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <SectionEyebrow>Our Facility</SectionEyebrow>
            <SectionTitle accent="Lanes">Featured</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
              State-of-the-art cricket lanes for both recreational play and professional training.
            </p>
          </div>
          <div className="max-w-md mx-auto">
            <div className="bg-spike-elevated border border-spike-blue/20 hover:border-spike-blue/50 overflow-hidden transition-colors" data-testid="featured-lane-card">
              <div className="relative">
                <img src="https://images.unsplash.com/photo-1531415074968-036ba1b575da?auto=format&fit=crop&w=900&q=80" alt="Cricket ball on grass" className="w-full h-56 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 bg-spike-blue text-black font-orbitron text-xs font-bold uppercase tracking-wider px-4 py-1.5">$35/hr</span>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-spike-blue/10 border border-spike-blue/30 text-spike-blue-light font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.2em] px-3 py-1">
                    <MapPin className="w-3 h-3" /> Mississauga, ON
                  </span>
                  <BookLink  className="bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2 transition-colors">
                    Book Now
                  </BookLink>
                </div>
                <h3 className="mt-4 font-orbitron font-black uppercase text-xl text-spike-text tracking-tight">Spike Sports — Mississauga</h3>
                <div className="mt-3 flex items-center gap-3 font-exo text-sm text-spike-text-muted">
                  <span className="inline-flex items-center gap-1"><Trophy className="w-4 h-4 text-spike-blue-light" /> 8 Lanes</span>
                  <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 text-spike-blue-light fill-spike-blue-light" /> 4.9</span>
                </div>
                <div className="mt-2 font-exo text-sm text-spike-text-muted flex items-start gap-1.5">
                  <MapPin className="w-4 h-4 text-spike-blue-light shrink-0 mt-0.5" />
                  7289 Torbram Rd., Mississauga, ON L4T 1G8
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRICKET PRO SHOP — hidden when show_shop feature flag is off */}
      {show_shop ? (
      <section className="relative py-20 lg:py-24 border-b border-spike-blue/10" data-testid="pro-shop-section">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <SectionEyebrow>Gear Up</SectionEyebrow>
            <SectionTitle accent="Pro Shop">Cricket</SectionTitle>
            <p className="mt-4 font-exo text-spike-text-muted text-lg max-w-2xl mx-auto">
              {shop_iframe_url
                ? "Browse our full catalogue right here — premium bats, balls, pads and protective gear."
                : "Discover our premium cricket equipment and accessories. Elevate your game with world-class bats, balls, and protective gear."}
            </p>
          </div>

          {shop_iframe_url ? (
            <div className="bg-spike-elevated border border-white/10 rounded-lg overflow-hidden" data-testid="pro-shop-iframe-wrap">
              <iframe
                src={shop_iframe_url}
                title="Spike Sports — Pro Shop"
                className="w-full h-[80vh] border-0 bg-white"
                loading="lazy"
                allow="payment; clipboard-read; clipboard-write"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pro-shop-teaser-grid">
              {proShopItems.map((p) => (
                <div key={p.name} className="bg-spike-elevated border border-white/5 hover:border-spike-blue/50 overflow-hidden group transition-colors" data-testid={`shop-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="relative overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <span className="absolute top-3 right-3 bg-spike-blue text-black font-orbitron text-[0.65rem] font-bold uppercase tracking-[0.18em] px-3 py-1">On Sale</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-orbitron font-bold uppercase tracking-[0.1em] text-base text-spike-text">{p.name}</h3>
                    <p className="mt-1 font-exo text-sm text-spike-text-muted">Starting from <span className="text-spike-blue-light font-bold">${p.price}</span></p>
                    <a href="https://spikesports.ca/shop" target="_blank" rel="noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-spike-blue text-spike-blue-light hover:bg-spike-blue hover:text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-4 py-2.5 transition-colors">
                      <ShoppingCart className="w-4 h-4" /> Buy Now
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      ) : null}

      {/* CTA STRIP */}
      <section className="py-16" data-testid="cta-strip">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="relative overflow-hidden bg-spike-elevated border border-spike-blue/30 px-8 py-12 lg:px-14 lg:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 shadow-card-hover">
            <div className="absolute inset-0 stripe-pattern opacity-50" />
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-spike-blue/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3 font-orbitron text-[0.7rem] font-bold text-spike-blue-light tracking-[0.4em] uppercase">
                <Zap className="w-3.5 h-3.5" /> Ready to play?
              </div>
              <h3 className="mt-3 font-orbitron font-black uppercase text-3xl lg:text-4xl text-spike-text tracking-tight">Lock in your lane today.</h3>
              <p className="mt-3 font-exo text-spike-text-muted max-w-xl">Real-time availability, instant confirmation, and a community that cheers louder than your last six.</p>
            </div>
            <BookLink
              
              data-testid="cta-strip-book"
              className="relative inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.2em] text-sm px-7 py-4 shadow-lg shadow-spike-blue/40 hover:shadow-spike-blue/70 transition-colors"
            >
              Book a Lane <ArrowRight className="w-5 h-5" />
            </BookLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
